import { compareDesc } from "date-fns";
import { createSelector } from "reselect";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import { ICannedResponseTag } from "components/message-box/canned-responses";
import { Right } from "constants/right";
import { IWithSessionState, hasRight } from "store/features/session";
import { createRequestFetchingSelector } from "store/requests/selectors";
import { getSelectedTeamId } from "store/views/settings";
import { KeyMap } from "utils/interface";

import { IWithAgentsState } from "../agents";
import { selectCurrentAgentAssignedTeams } from "../teams";

import { CannedResponseRequestActions } from "./actions";
import { ALL_TEAMS_ID } from "./constants";
import { caseInsensitiveCompare } from "./helpers";
import { ICannedResponse, ICannedResponsesState } from "./interfaces";

interface IWithCannedResponsesState {
  entities: {
    cannedResponses: ICannedResponsesState;
  };
}

function sortByDates(a: ICannedResponse, b: ICannedResponse): number {
  const dateA = a.updatedAt || a.createdAt;
  const dateB = b.updatedAt || b.createdAt;
  return compareDesc(dateA, dateB);
}

export function selectCannedResponses(state: IWithCannedResponsesState): { [key: string]: ICannedResponse } {
  return state.entities.cannedResponses.byIds;
}

export const selectCannedResponsesArray = createSelector([selectCannedResponses], cannedResponses =>
  Object.values(cannedResponses)
);

export const selectAgentCannedResponses = createSelector(
  [selectCannedResponses, selectCurrentAgentAssignedTeams],
  (cannedResponses: { [key: string]: ICannedResponse }, currentAgentTeams: Array<{ id: string }>) => {
    const result: { [key: string]: ICannedResponse } = {};
    const currentAgentTeamIds = new Set(currentAgentTeams.map(team => team.id));

    for (const shortcut of Object.keys(cannedResponses)) {
      const response = cannedResponses[shortcut];
      if (response.teamId === null || currentAgentTeamIds.has(response.teamId)) {
        result[shortcut] = response;
      }
    }

    return result;
  }
);

function selectCannedResponsesIdsByShortcuts(state: IWithCannedResponsesState): KeyMap<string[]> {
  return state.entities.cannedResponses.idsByShortcuts;
}

export const getSettingsCannedResponses = createSelector(
  [selectCannedResponses, getSelectedTeamId],
  (cannedResponses, selectedTeamId): ICannedResponse[] => {
    const teamId = selectedTeamId === ALL_TEAMS_ID ? null : selectedTeamId;
    const values = Object.values(cannedResponses);
    const filteredByTeamId = teamId ? values.filter(cannedResponse => cannedResponse.teamId === teamId) : values;

    return filteredByTeamId.sort(sortByDates);
  }
);

function canManageSharedCannedResponses(state: IWithSessionState & IWithAgentsState): boolean {
  return hasRight(state, Right.ManageSharedCannedResponses);
}

export const getCanManageCannedResponses = createSelector(
  [getSelectedTeamId, canManageSharedCannedResponses],
  (selectedTeamId, manageSharedCannedResponses): boolean => {
    if (selectedTeamId === ALL_TEAMS_ID && !manageSharedCannedResponses) {
      return false;
    }

    return true;
  }
);

export const selectAgentCannedResponsesByShortcuts = createSelector(
  [selectAgentCannedResponses, selectCannedResponsesIdsByShortcuts],
  (agentCannedResponses, idsByShortcuts): ICannedResponseTag[] => {
    const result: ICannedResponseTag[] = [];

    Object.keys(idsByShortcuts)
      .sort(caseInsensitiveCompare)
      .forEach(shortcut => {
        const values = idsByShortcuts[shortcut]
          .filter(id => id in agentCannedResponses)
          .map(id => ({
            id,
            richTextObj: agentCannedResponses[id].richTextObj,
            text: agentCannedResponses[id].text,
            attachments: agentCannedResponses[id].attachments,
          }));

        if (values.length > 0) {
          result.push({ name: shortcut, values });
        }
      });

    return result;
  }
);

export const selectIsCannedResponsesFetching = createRequestFetchingSelector([
  CannedResponseRequestActions.FETCH_COLLECTION,
]);
